import type { ReactElement } from 'react'

import type { TypeHorizontalAlignment } from '@contentful-types/alignment'
import { type TypeSectionContentItems } from '@contentful-types/section'
import type { TypeSectionTwoColumnEntry } from '@contentful-types/section-two-column'

import { CtaDescriptionPosition, CtaDescriptionWidth } from '@constants/enums/cta'
import { SingleColBeforeBreakpoints, TwoColumnGapSize } from '@constants/enums/layout'

import {
  CtaButtonMetadataProvider,
  DefaultCtaButtonContext,
} from '../../../contexts/cta-button-context'
import { TwoColumnLayout } from '../../layout/two-column/two-column'
import { PageSection } from '../../page-core/section/page-section'
import { ContentRenderer } from '../../renderer'

import styles from './two-column.module.scss'

const alignmentStyleMap: Record<TypeHorizontalAlignment, string> = {
  right: styles.alignRight,
  left: styles.alignLeft,
  centerContentLeftAlignText: '',
  default: '',
}

function TwoColumnSectionContent(props: TypeSectionTwoColumnEntry): ReactElement {
  const {
    fields: {
      leftColumnAlignment,
      leftColumnContent,
      rightColumnAlignment,
      rightColumnContent,
      columnsProportion,
      reverseOrderWhenVertical,
      gutterGap,
      collapseToSingleColumnBreakpoint,
    },
  } = props

  const leftAlignmentStyle = alignmentStyleMap[leftColumnAlignment?.fields.horizontal!] ?? ''
  const rightAlignmentStyle = alignmentStyleMap[rightColumnAlignment?.fields.horizontal!] ?? ''

  return (
    <TwoColumnLayout
      reverseOrderWhenVertical={reverseOrderWhenVertical}
      singleColBefore={collapseToSingleColumnBreakpoint}
      columnRatio={columnsProportion}
      gap={gutterGap && TwoColumnGapSize[gutterGap]}
      leftColumnVerticalAlignment={leftColumnAlignment?.fields.vertical}
      leftContent={
        <div className={leftAlignmentStyle}>
          <ContentRenderer data={leftColumnContent as TypeSectionContentItems[] | undefined} />
        </div>
      }
      rightColumnVerticalAlignment={rightColumnAlignment?.fields.vertical}
      rightContent={
        <div className={rightAlignmentStyle}>
          <ContentRenderer data={rightColumnContent as TypeSectionContentItems[] | undefined} />
        </div>
      }
    />
  )
}

export function TwoColumnSection({ data }: { data: TypeSectionTwoColumnEntry }): ReactElement {
  const {
    sys,
    fields: {
      internalName,
      background,
      heading,
      collapseToSingleColumnBreakpoint = SingleColBeforeBreakpoints.Small,
    },
  } = data

  const descriptionWidth =
    collapseToSingleColumnBreakpoint === SingleColBeforeBreakpoints.Small
      ? CtaDescriptionWidth.Fixed
      : CtaDescriptionWidth.Auto

  return (
    <PageSection
      internalName={internalName}
      sysStructure={sys}
      heading={heading}
      background={background}
    >
      <CtaButtonMetadataProvider
        value={{
          ...DefaultCtaButtonContext,
          descriptionWidth,
          descriptionPosition: CtaDescriptionPosition.Left,
        }}
      >
        <TwoColumnSectionContent {...data} />
      </CtaButtonMetadataProvider>
    </PageSection>
  )
}

export function TwoColumnSectionForTabs(): string {
  return 'PLACEHOLDER: two column section for tabs'
}
